import React from 'react';
import MathText from '../component/common/MathText';

// ===== REGEX PATTERNS =====
export const IMG_TAG_REGEX = /(<img\b[^>]*>)/gi;

// ===== VALIDATION FUNCTIONS =====

/**
 * Kiểm tra nhanh chuỗi có chứa thẻ HTML không để quyết định cách render
 */
export const containsHtmlTag = (value) => /<[^>]+>/.test(String(value || ''));

/**
 * Kiểm tra có chứa thẻ <img> không
 */
export const containsImageTag = (value) => /<img\b[^>]*>/i.test(String(value || ''));

/**
 * Kiểm tra có chứa công thức toán học (LaTeX/MathML) không
 */
export const containsMathContent = (value) => {
    const input = String(value || '');
    return /<math\b[\s\S]*?<\/math>/i.test(input)
        || /\$\$[\s\S]+?\$\$|\$[\s\S]+?\$/.test(input)
        || /\\(?:frac|sqrt|left|right|begin|end|matrix|sum|int|alpha|beta|gamma)\b/.test(input)
        || /\\[a-zA-Z]+/.test(input)
        || /[A-Za-z0-9)}\]]\s*[\^_]\s*\*?\s*(?:\{|[A-Za-z0-9\\])/.test(input)
        || /\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)/.test(input);
};

// ===== SANITIZATION FUNCTIONS =====

/**
 * Sanitize HTML từ DOCX: xóa script/style, chuẩn hóa URL, thêm style cho img/table
 */
export const sanitizeDocxHtml = (unsafeHtml) => {
    if (!unsafeHtml || typeof DOMParser === 'undefined') {
        return '';
    }

    const doc = new DOMParser().parseFromString(String(unsafeHtml), 'text/html');

    doc.querySelectorAll('script, style, iframe, object, embed, link, meta').forEach((node) => node.remove());

    doc.querySelectorAll('*').forEach((node) => {
        Array.from(node.attributes).forEach((attr) => {
            const name = attr.name.toLowerCase();
            const value = attr.value || '';

            if (name.startsWith('on')) {
                node.removeAttribute(attr.name);
                return;
            }

            if (name === 'href' || name === 'src') {
                const isImgTag = node.tagName.toLowerCase() === 'img';
                const isSafeUrl = value.startsWith('http://')
                    || value.startsWith('https://')
                    || (isImgTag && (value.startsWith('data:') || value.startsWith('blob:') || value.startsWith('cid:')))
                    || (!isImgTag && value.startsWith('data:image/'));
                if (!isSafeUrl) {
                    node.removeAttribute(attr.name);
                }
            }
        });

        if (node.tagName.toLowerCase() === 'img') {
            node.setAttribute('style', 'max-width: 100%; height: auto; display: block; margin-top: 6px; margin-bottom: 6px;');
        }
        // Bảng từ Word/mammoth xuất ra thường không có border sẵn, cần thêm style
        // để người đọc dễ phân biệt các ô với nhau.
        if (node.tagName.toLowerCase() === 'table') {
            node.setAttribute('style', 'border-collapse: collapse; width: auto; margin: 3px 0;');
        }
        if (node.tagName.toLowerCase() === 'td' || node.tagName.toLowerCase() === 'th') {
            node.setAttribute('style', 'border: 1px solid #999; padding: 4px 5px;');
        }
    });

    return doc.body.innerHTML;
};

// ===== STYLE PARSING FUNCTIONS =====

/**
 * Chuyển chuỗi "key: value; key2: value2" thành object cho React style prop
 */
export const parseInlineStyleToObject = (styleString) => {
    const result = {};
    String(styleString || '')
        .split(';')
        .forEach((declaration) => {
            const separatorIndex = declaration.indexOf(':');
            if (separatorIndex === -1) return;
            const rawProperty = declaration.slice(0, separatorIndex).trim();
            const rawValue = declaration.slice(separatorIndex + 1).trim();
            if (!rawProperty || !rawValue) return;
            const camelProperty = rawProperty.startsWith('--')
                ? rawProperty
                : rawProperty.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
            result[camelProperty] = rawValue;
        });
    return result;
};

// ===== DOM TO REACT CONVERSION =====

// Counter để tạo unique key cho các React element
let richTextKeyCounter = 0;

/**
 * Duyệt cây DOM: giữ nguyên mọi thẻ cấu trúc (table/tr/td/th/img/ul/li/p/span/strong/u...)
 * Chỉ text node nào chứa công thức toán mới được bọc bằng MathText
 */
export const domNodeToReact = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
        const value = node.textContent || '';
        if (containsMathContent(value)) {
            return <MathText key={`drt-math-${richTextKeyCounter++}`} text={value} />;
        }
        return value;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return null;
    }

    const tagName = node.tagName.toLowerCase();

    if (tagName === 'math') {
        return (
            <span
                key={`drt-math-el-${richTextKeyCounter++}`}
                dangerouslySetInnerHTML={{ __html: node.outerHTML }}
            />
        );
    }

    const props = { key: `drt-el-${richTextKeyCounter++}` };
    Array.from(node.attributes || []).forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (name === 'style') {
            props.style = parseInlineStyleToObject(attr.value);
            return;
        }
        props[name === 'class' ? 'className' : name] = attr.value;
    });

    if (tagName === 'img' || tagName === 'br' || tagName === 'hr') {
        return React.createElement(tagName, props);
    }

    const children = Array.from(node.childNodes || [])
        .map(domNodeToReact)
        .filter((c) => c !== null && c !== '');

    return React.createElement(tagName, props, children.length ? children : undefined);
};

/**
 * Parse chuỗi HTML đã sanitize thành cây React, giữ nguyên table/img,
 * chỉ wrap MathText cho text node có công thức
 */
export const renderSanitizedHtml = (safeHtml) => {
    if (typeof DOMParser === 'undefined') {
        return <span dangerouslySetInnerHTML={{ __html: safeHtml }} />;
    }
    richTextKeyCounter = 0;
    const doc = new DOMParser().parseFromString(String(safeHtml || ''), 'text/html');
    const children = Array.from(doc.body.childNodes).map(domNodeToReact).filter((c) => c !== null && c !== '');
    return <>{children}</>;
};

/**
 * Reset counter - gọi trước khi render để tránh key trùng
 */
export const resetRichTextKeyCounter = () => {
    richTextKeyCounter = 0;
};

/**
 * Get current counter value - dùng cho các component cần lấy giá trị hiện tại
 */
export const getRichTextKeyCounter = () => richTextKeyCounter;

/**
 * Set counter value - dùng để khởi tạo counter với giá trị cụ thể
 */
export const setRichTextKeyCounter = (value) => {
    richTextKeyCounter = value;
};
